/**
 * IndexedDB Service for Offline Storage
 * Stores transactions, cart items, and pending sync operations
 */

const DB_NAME = 'PhoneLabsPOS';
const DB_VERSION = 1;

// Object stores
const STORES = {
  PENDING_SALES: 'pendingSales',
  OFFLINE_CART: 'offlineCart',
  SYNC_QUEUE: 'syncQueue',
  CACHED_CATALOG: 'cachedCatalog'
};

interface PendingSale {
  id: string;
  timestamp: number;
  saleData: any;
  status: 'pending' | 'syncing' | 'failed';
  retryCount: number;
  lastError?: string;
}

interface SyncQueueItem {
  id: string;
  type: 'sale' | 'mutation' | 'return';
  endpoint: string;
  data: any;
  timestamp: number;
  retryCount: number;
}

class IndexedDBService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains(STORES.PENDING_SALES)) {
          const salesStore = db.createObjectStore(STORES.PENDING_SALES, { keyPath: 'id' });
          salesStore.createIndex('timestamp', 'timestamp', { unique: false });
          salesStore.createIndex('status', 'status', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.OFFLINE_CART)) {
          db.createObjectStore(STORES.OFFLINE_CART, { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const queueStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
          queueStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.CACHED_CATALOG)) {
          const catalogStore = db.createObjectStore(STORES.CACHED_CATALOG, { keyPath: 'id' });
          catalogStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
      };
    });
  }

  private ensureDB(): IDBDatabase {
    if (!this.db) {
      // Try to initialize if not already done
      console.warn('⚠️ IndexedDB not initialized, skipping offline storage');
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  // Check if DB is initialized (non-throwing version)
  isInitialized(): boolean {
    return this.db !== null;
  }

  // Pending Sales Operations
  async savePendingSale(saleData: any): Promise<string> {
    const db = this.ensureDB();
    const id = `sale_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const pendingSale: PendingSale = {
      id,
      timestamp: Date.now(),
      saleData,
      status: 'pending',
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const request = store.add(pendingSale);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSales(): Promise<PendingSale[]> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updatePendingSaleStatus(
    id: string,
    status: 'pending' | 'syncing' | 'failed',
    error?: string
  ): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (sale) {
          sale.status = status;
          if (status === 'failed') {
            sale.retryCount += 1;
            sale.lastError = error;
          }
          const updateRequest = store.put(sale);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Sale not found'));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deletePendingSale(id: string): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Sync Queue Operations
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
    const db = this.ensureDB();
    const id = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const queueItem: SyncQueueItem = {
      ...item,
      id,
      timestamp: Date.now(),
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(queueItem);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeFromSyncQueue(id: string): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Cached Catalog Operations
  async cacheCatalogItems(items: any[]): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHED_CATALOG], 'readwrite');
      const store = transaction.objectStore(STORES.CACHED_CATALOG);

      // Clear existing cache
      store.clear();

      // Add new items
      items.forEach(item => {
        store.add({
          ...item,
          lastUpdated: Date.now()
        });
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCachedCatalog(): Promise<any[]> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHED_CATALOG], 'readonly');
      const store = transaction.objectStore(STORES.CACHED_CATALOG);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Offline Cart Operations
  async saveOfflineCart(cartData: any): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      const request = store.put({ id: 'current', data: cartData, timestamp: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineCart(): Promise<any | null> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readonly');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      const request = store.get('current');

      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  }

  async clearOfflineCart(): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      const request = store.delete('current');

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Utility
  async clearAllData(): Promise<void> {
    const db = this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORES.PENDING_SALES, STORES.OFFLINE_CART, STORES.SYNC_QUEUE, STORES.CACHED_CATALOG],
        'readwrite'
      );

      Object.values(STORES).forEach(storeName => {
        transaction.objectStore(storeName).clear();
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getStats(): Promise<{
    pendingSales: number;
    syncQueue: number;
    cachedItems: number;
  }> {
    const [pendingSales, syncQueue, cachedCatalog] = await Promise.all([
      this.getPendingSales(),
      this.getSyncQueue(),
      this.getCachedCatalog()
    ]);

    return {
      pendingSales: pendingSales.length,
      syncQueue: syncQueue.length,
      cachedItems: cachedCatalog.length
    };
  }
}

// Export singleton instance
export const indexedDBService = new IndexedDBService();
export default indexedDBService;
